
import { Options, Vue } from 'vue-class-component'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons'
import CoinHistoryHeader from './components/header/coin-history-header.vue'
import LoadingCoinHistory from './components/loading/loading.vue'
import Error from './components/error/error.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { useStore } from '@/store'
import { generatePastMonths, getDate } from '@/helpers/date'
import * as meService from '@/services/line-web-loyalty-bff/v1/me'
import { getLocalStorage, removeLocalStorage } from '@/helpers/cacheStorage'
import liff from '@line/liff/dist/lib'
import { TieredPoint } from '@/interfaces/customer-info'
import { getDisplayPoint } from '@/utils/functions'
import { Watch } from 'vue-property-decorator'
import { mapState } from 'vuex'
import { ACTIVITY_POS_TYPE, ACTIVITY_REDEEM_TYPE, ACTIVITY_TYPE } from '../../model/coin-transaction-type.model'

library.add(faChevronLeft)
interface History {
  id: string;
  modification: number;
  modificationType: string;
  activityType: string;
  timeOfOccurrence: string;
  storeName: string;
  pointSourceName: string;
  pointAccountId: string;
  pointAccountName: string;
  reasonCode: null;
}

@Options({
  components: {
    FontAwesomeIcon,
    CoinHistoryHeader,
    Error,
    LoadingCoinHistory,
  },
  computed: {
    ...mapState({
      selectedMonth: (state: any) => state.coinHistory.selectedMonth,
    })
  },
})
export default class CoinHistoryPage extends Vue {
  private store = useStore();
  private isModalClose = false
  private coinHistoryList: any = [];
  private isLoading = true;
  private isLoadingPoints = true;
  private isPointsError = false;
  private originalCoinHistoryList: any = [];
  private active = 'all';
  private activeMonthState: 'primary' | 'secondary' = 'primary'
  private activeMonthPrimary = ''
  private activeMonthSecondary = ''
  private idToken = '';
  private keywordEarn = /Earn/;
  private keywordTransfer = /Transfer/;
  private keywordRevert = /Revert/;
  private keywordIn = /In/;
  private keywordTMN = /ML_TMN/
  private error: null | number = null;
  private refCodeError: string | null = null;

  // these constants must align with css of the elements
  private maximumFilterHeight = 102 + 52;
  private headerHeight = 99;
  private menuHeight = 52;
  private startedPagePaddingTop = this.headerHeight + this.menuHeight;
  private startedTop = 99;
  private startedFiltersTop = this.menuHeight;
  private menuElement: HTMLElement | null = null;
  private dummyPaddingTop: HTMLElement | null = null;
  private listContainer: HTMLElement | null = null;
  private filterContainer: HTMLElement | null = null;
  private lastScrollTop = 0;
  private currentFiltersTop = this.startedFiltersTop;
  private currentMenuTop = this.startedTop;
  private currentDummyPaddingTopHeight = this.startedPagePaddingTop;
  private isDisabledScrollbar = false;
  private isScrollUp = false;
  private activeIndexMonth: null | number = null
  elementListContainer: HTMLElement | null = null
  elementCoinHistoryList: HTMLElement | null = null
  elementListContainerTop = 100
  elementClassListContainer: HTMLElement[] | null = null
  private limitCoinHistory = 100;
  async created() {
    this.getCoinHistory()
    this.getPointBalance()
  }

  handleRedirectApp() {
    const query = this.$route?.query

    if(query['is_disabled_deeplink'] !== 'true') {
      window.location.href = process.env.VUE_APP_DOWNLOAD_LOTUSS!
    }
  }

  onRefresh() {
    this.getCoinHistory()
    this.getPointBalance()
  }

  @Watch('activeIndexMonth')
  onActiveIndexMonthChange(newValue: number | null) {
    if (newValue === null || this.activeIndexMonth === null) {
      this.activeMonthPrimary = ''
      this.activeMonthSecondary = ''
      return
    }
    try {
      const monthLabel = Object.keys(this.coinHistoryList[this.activeIndexMonth!])[0]
      if (this.activeMonthState === 'primary') {
        this.activeMonthSecondary = monthLabel
        this.activeMonthState = 'secondary'
      } else if (this.activeMonthState === 'secondary') {
        this.activeMonthPrimary = monthLabel
        this.activeMonthState = 'primary'
      }
    } catch (e) {
      console.log()
    }
  }

  isReachedLimitCoinHistory (coinHistoryLength: number) {
    return coinHistoryLength >= this.limitCoinHistory
  }

  async getCoinHistory(offset = 0) {
    this.isLoading = true

    if (this.store.state.lineInfo.idToken) {
      this.idToken = this.store.state.lineInfo.idToken
      const date = new Date()
      const fromDate = new Date(date.getFullYear(), date.getMonth() - 11)

      const params = {
        offset,
        limit: this.limitCoinHistory,
        fromDate: fromDate.toISOString(),
        toDate: date.toISOString(),
      }

      const result = await meService.getCoinHistory(this.idToken, params)
      if (result.httpStatusCode !== 200) {
        this.error = result.httpStatusCode
        this.refCodeError = getLocalStorage('x-request-id')
        this.activeIndexMonth = null
        removeLocalStorage('x-request-id')
      } else {
        const historyInOffset = result.json.data as History[]
        const data = this.convertCoinHistoryList(result.json.data)
        this.coinHistoryList = this.coinHistoryList.concat(data)
        this.originalCoinHistoryList = this.coinHistoryList.concat(data)

        if (this.isReachedLimitCoinHistory(historyInOffset.length)) {
          this.getCoinHistory((offset ?? 0) + this.limitCoinHistory)
        }
        if (this.coinHistoryList.length > 0) {
          this.activeIndexMonth = 0
        }
      }
      this.isLoading = false
    }
  }

  updateStoredPoints(points: number, tieredPoints: TieredPoint[]) {
    const customer = this.store.state.customer
    customer.points = points
    customer.tieredPoints = tieredPoints
    this.store.commit('customer/updateCustomer', customer)
  }

  async getPointBalance() {
    this.isPointsError = false
    this.isLoadingPoints = true
    let result

    if (this.store.state.lineInfo.idToken) {
      this.idToken = this.store.state.lineInfo.idToken
      result = await meService.getCustomerInfo(this.idToken, ['points'])
    }

    if (result?.httpStatusCode === 200) {
      const { points, tiered_points: tieredPoints } = result.json.data
      this.updateStoredPoints(points, tieredPoints)
      this.isLoadingPoints = false
    } else {
      if (result?.httpStatusCode === 401) {
        liff.logout()
        liff.login()
      } else {
        this.error = result?.httpStatusCode || 500
        this.refCodeError = getLocalStorage('x-request-id')
        removeLocalStorage('x-request-id')
        this.isPointsError = true
        this.isLoadingPoints = false
      }
    }
  }

  getLabelPoints(points: number): string {
    return points !== undefined ? getDisplayPoint(points) : 'N/A'
  }

  getMonth(date: string) {
    return getDate(date, 'MMM BBBB')
  }

  convertCoinHistoryList(coinHistorysData: History[] | []) {
    const months = generatePastMonths()
    const newCoinHistorysData = months.map((month) => {
      const history = coinHistorysData.filter(
        (coinHistory) =>
          getDate(coinHistory.timeOfOccurrence, 'MMM BBBB') === month
      )
      if (history.length) {
        return { [month]: history }
      }
      return { [month]: [] }
    })
    return newCoinHistorysData
  }

  isInitElement = false
  previousScrollY = 0
  isShowingFixedMonthLabel = false

  initElements () {
    this.isInitElement = true
    this.elementListContainer = document.getElementById('list-container')
    this.elementClassListContainer = document.getElementsByClassName('top') as any
    this.elementCoinHistoryList = document.getElementById('coin-history-list')
  }

  previousTouchY = 0
  isEnabledContainerMovement = true
  isTouching = false
  isHighlightMonthActive = false
  isListContainerMovable = true
  coefficientOfMovement = 0.7

  onScroll (event: any) {
    if (!this.isInitElement) {
      this.initElements()
    }

    const { scrollTop } = event.currentTarget
    const scroll: 'up' | 'down' = scrollTop >= this.previousScrollY ? 'down' : 'up'

    const dif = scrollTop - this.previousScrollY
    // to move up/down the container of list
    if (scroll === 'down' && this.elementListContainerTop > 0) {
      this.elementListContainerTop -= (this.coefficientOfMovement)*dif
      if (this.elementListContainerTop < 0) this.elementListContainerTop = 0
    } else if (scroll === 'up' && this.elementListContainerTop < 100) {
      this.elementListContainerTop -= (this.coefficientOfMovement)*dif
      if (this.elementListContainerTop < 0) {
        this.elementListContainerTop = 0
      }
      if (this.elementListContainerTop > 100) this.elementListContainerTop = 100
    }

    // Handle border radius of modal
    if(this.elementClassListContainer) {
      if(this.elementListContainerTop <= 0) {
        this.elementClassListContainer[0].style.borderRadius = '0'
      } else {
        this.elementClassListContainer[0].style.borderRadius = '24px 24px 0 0'
      }
    }

    this.elementListContainer!.style.top = `${this.elementListContainerTop}px`

    // to show/hide highlight month label
    if (scroll === 'down' && this.elementListContainerTop >= 75) {
      this.isHighlightMonthActive = true
    } else if (scroll === 'up' && this.elementListContainerTop >= 99 && scrollTop <= 50) {
      this.isHighlightMonthActive = false
    }

    this.previousScrollY = scrollTop
    this.calculateLabelMonth(scroll)
  }

  // DONT DELETE THIS COMMENTED !! Please keep it for improvement in the feature
  // globalTouchState: 'up' | 'down' = 'up'
  // onTouchMove (event: any) {
  //   this.isTouching = true
  //   if (event.touches.length > 1) return

  //   // Get the first touch's position
  //   const touch = event.touches[0]
  //   const { clientY } = touch
  //   const touchState: 'up' | 'down' = clientY >= this.touchingPositionY ? 'down' : 'up'
  //   this.globalTouchState = touchState
  //   const dif = this.touchingPositionY - clientY

  //   if (touchState === 'up') {
  //     if (this.elementListContainerTop > 0 && this.isEnabledContainerMovement) {
  //       this.isHighlightMonthActive = true
  //       this.elementListContainerTop -= dif

  //       // hit top position and show fixed month label
  //       this.isShowingFixedMonthLabel = true
  //     }
  //   } else if (touchState === 'down') {
  //     if (this.elementListContainerTop <= 0) {
  //       if (this.elementCoinHistoryList!.scrollTop <= 0) {
  //         this.isEnabledContainerMovement = true
  //         this.elementCoinHistoryList!.style.overflowY = 'hidden'
  //       }
  //     } else {
  //       this.isHighlightMonthActive = false
  //     }
  //     if (this.elementListContainerTop <= 100 && this.isEnabledContainerMovement) {
  //       this.elementListContainerTop -= dif
  //     }
  //   }

  //   // switch to scroll a scrollbar of list instead of moving the container without re-touching
  //   if (this.elementListContainerTop <= 0) {
  //     this.isEnabledContainerMovement = false
  //     this.elementCoinHistoryList!.style.overflowY = 'auto'
  //     this.elementCoinHistoryList!.scrollTop += dif
  //   }

  //   if (this.isEnabledContainerMovement) {
  //     this.elementListContainer!.style.top = `${this.elementListContainerTop}px`
  //   }

  //   this.calculateLabelMonth()
  //   this.touchingPositionY = clientY
  // }

  isTouchingPointHistoryBody = false
  touchingPositionY = 0

  calculateLabelMonth(scroll: 'up' | 'down') {
    if (this.activeIndexMonth === null) return
    
    const targetMonthIndex = scroll === 'down' ? (this.activeIndexMonth + 1) : (this.activeIndexMonth)
    const currentActiveElement = document.getElementById(`month_${targetMonthIndex}`)
    if (!currentActiveElement) return
    const elementPositionYRelatedWithContainer = currentActiveElement!.getBoundingClientRect().y - 100

    if (scroll === 'down' && elementPositionYRelatedWithContainer <= 0) {
      this.activeIndexMonth += 1
    } else if (scroll === 'up' && elementPositionYRelatedWithContainer >= 0) {
      this.activeIndexMonth -= 1
    }
    this.store.commit('coinHistory/updateSelectedCalendar', this.activeIndexMonth)   
  }

  onTouchEndCoinHistoryBody () {
    this.isTouching = false
  }

  getPositionRelativeToContainerWithScroll(element: any) {
    // Ensure you have the right references for element and its container
    var container = element.offsetParent
    var elementRect = element.getBoundingClientRect()
    var containerRect = container.getBoundingClientRect()

    // Calculate relative position, including the scroll positions
    return {
      left: (elementRect.left - containerRect.left) + container.scrollLeft,
      top: (elementRect.top - containerRect.top) + container.scrollTop
    }
  }

  closeModal() {
    this.isModalClose = true
    
    setTimeout(() => {
      this.store.dispatch('coinHistory/onClose', true)
    }, 500)
  }

  filterActivity(activeType: string, point: number) {
    const MallLoyalty = 'MallLoyalty.Earn'
    const coinAdjustmentActive = [
      'Coin.Behavior.Campaign.Refund',
      'Coin.Manual.PointAdjust.Add',
      'Behavior.Campaign.Refund',
      'Manual.PointAdjust.Add',
    ]
    const coinAdjustmentInActive = [
      'Coin.Manual.PointAdjust.Deduct',
      'Coin.Refund',
      'Point.Refund',
      'Manual.PointAdjust.Deduct',
    ]
    const earn = ['Incent.Outcomes.Earn', 'Behavior.Campaign.Earn']
    const campaignPoint = [
      'Staff.Point.Earn',
      'Behavior.Campaign.Earn',
      'Campaign.Point.Earn',
      'Survey.Point.Earn',
      'ShopMarathon.Earn.Game',
    ]

    if (activeType) {
      if (activeType.match(this.keywordRevert)) {
        return activeType.match(this.keywordIn) ? true : false
      } else if (activeType.match(this.keywordTransfer)) {
        return activeType.match(this.keywordIn) ? true : false
      } else if (activeType.match(this.keywordEarn)) {
        return true
      } else if (
        earn.includes(activeType) ||
        campaignPoint.includes(activeType) ||
        activeType.match(MallLoyalty) ||
        coinAdjustmentActive.includes(activeType) ||
        ([ACTIVITY_TYPE.LOYALTYGAME_EARN_GAME, ACTIVITY_TYPE.POS_REFUND, ACTIVITY_TYPE.POS_VOID].includes(activeType as ACTIVITY_TYPE) && point > 0)
      ) {
        return true
      } else if (
        'Point.Expired' === activeType ||
        coinAdjustmentInActive.includes(activeType) ||
        ACTIVITY_REDEEM_TYPE.includes(activeType as ACTIVITY_TYPE) ||
        (ACTIVITY_POS_TYPE.includes(activeType as ACTIVITY_TYPE) && point < 0)
      ) {
        return false
      }
    }

    return null
  }

  filterActivityList(menu: string, historys: History[]) {
    const filtedActivity = historys.filter((data: History) => {
      if (!data?.activityType) return false
      const isActivity = this.filterActivity(data.activityType, data.modification)
      return menu === 'earn' ? isActivity : !isActivity
    })
    return filtedActivity
  }

  onSelected(menu: string) {
    let newCoinHistorys: any = []

    this.originalCoinHistoryList.forEach((historys: any) => {
      const key = Object.keys(historys)[0]
      let result: History[] = []
      if (historys[key].length && (menu === 'earn' || menu === 'burn')) {
        result = this.filterActivityList(menu, historys[key])
      }
      newCoinHistorys.push({ [key]: result })
    })

    this.active = menu
    this.coinHistoryList =
      menu === 'all' ? this.originalCoinHistoryList : newCoinHistorys
  }

  getDate(date: string) {
    return getDate(date, 'DD MMM BBBB')
  }

  getNote(history: History) {
    const { activityType, storeName, reasonCode, modificationType, pointSourceName } = history
    const IncentOutcomesEarn = 'Incent.Outcomes.Earn'
    const IncentOutcomes = 'INCENT.Outcomes'
    const MallLoyalty = 'MallLoyalty.Earn'
    const types = [
      'Coin.Behavior.Campaign.Refund',
      'Coin.Manual.PointAdjust.Add',
      'Coin.Manual.PointAdjust.Deduct',
      'Coin.Refund',
      'Behavior.Campaign.Refund',
      'Manual.PointAdjust.Add',
      'Point.Refund',
      'Manual.PointAdjust.Deduct',
      'Incent.Outcomes.Earn',
      'Staff.Point.Earn',
    ]

    if (
      modificationType === IncentOutcomes &&
      activityType === IncentOutcomesEarn &&
      pointSourceName.match(this.keywordTMN)?.length
    ) {
      return history.reasonCode || ''
    }
    else if (activityType.match(MallLoyalty)) {
      const sections = activityType.split('.')
  
      return sections[2]
    } 
    else if (activityType === 'Incent.Outcomes.Redeem')
      return 'รายการปรับปรุงโลตัสคอยน์'
    else if (activityType === 'Behavior.Campaign.Earn') {
      return reasonCode || storeName
    }
    else if (
      activityType === 'ShopMarathon.Earn.Game' || 
      modificationType === 'ShopMarathon.Earn.Game' ||
      (
        [ACTIVITY_TYPE.LOYALTYGAME_EARN_GAME].includes(activityType as ACTIVITY_TYPE) &&
        [ACTIVITY_TYPE.LOYALTYGAME_EARN_GAME].includes(modificationType as ACTIVITY_TYPE)
      )
    ) {
      return 'รางวัลจากการเล่นเกมส์'
    }
    else if (activityType === ACTIVITY_TYPE.PARTNER_REDEEM) return 'ไลฟ์สไตล์รีวอร์ด'
    else if (activityType === ACTIVITY_TYPE.REWARD_STORE_REDEEM)
      return 'โลตัสรีวอร์ด'
    else if (activityType.match(this.keywordTransfer)) {
      const segment1 = activityType.match(this.keywordIn) ? 'เข้าจาก' : 'ออกไป'
      const partnerName = activityType.split('.')
      return `โอนคอยน์${segment1} ${partnerName[2]}`
    } else if (activityType.match(this.keywordRevert)) {
      const partnerName = activityType.split('.')
      return `ยกเลิกรายการจาก ${partnerName[2]}`
    } else if (types.includes(activityType)) {
      return storeName
    } else if (activityType === ACTIVITY_TYPE.POS_REFUND) {
      return 'คืนคอยน์ (คืนสินค้า)'
    } else if (activityType === ACTIVITY_TYPE.POS_VOID) {
      return 'คืนคอยน์'
    } else if (activityType === ACTIVITY_TYPE.POS_SPEND) {
      return 'แลกโลตัสคอนย์เป็นส่วนลด'
    }
    
    return null
  }

  getActivity(history: History) {
    const coinAdjustmentActiveImg = 'coin-adjustment-in.png'
    const coinAdjustmentInActiveImg = 'coin-adjustment-out.png'
    const IncentOutcomesEarn = 'Incent.Outcomes.Earn'
    const IncentOutcomes = 'INCENT.Outcomes'
    const MallLoyalty = 'MallLoyalty.Earn'

    const coinAdjustmentInActive = [
      'Coin.Manual.PointAdjust.Deduct',
      'Coin.Refund',
      'Point.Refund',
      'Manual.PointAdjust.Deduct',
    ]
    const earn = ['Incent.Outcomes.Earn']
    const campaignPoint = [
      'Staff.Point.Earn',
      'Behavior.Campaign.Earn',
      'Campaign.Point.Earn',
      'Survey.Point.Earn',
      'ShopMarathon.Earn.Game',
    ]
    const { activityType, modification, pointSourceName, modificationType } = history

    if (     
      modificationType === IncentOutcomes &&
      activityType === IncentOutcomesEarn &&
      pointSourceName.match(this.keywordTMN)
    ) {
      return 'campaign-point.png'
    } else if (activityType.match(this.keywordRevert)) {
      return activityType.match(this.keywordIn)
        ? coinAdjustmentActiveImg
        : coinAdjustmentInActiveImg
    } else if (activityType.match(this.keywordTransfer)) {
      return activityType.match(this.keywordIn)
        ? 'coin-transfer-in.png'
        : 'coin-transfer-out.png'
    } else if (
      [
        ACTIVITY_TYPE.COIN_BEHAVIOR_CAMPAIGN_REFUND,
        ACTIVITY_TYPE.COIN_MANUAL_POINTADJUST_ADD,
        ACTIVITY_TYPE.BEHAVIOR_CAMPAIGN_REFUND,
        ACTIVITY_TYPE.MANUAL_POINTADJUST_ADD
      ].includes(activityType as ACTIVITY_TYPE) ||
      ([ACTIVITY_TYPE.POS_VOID, ACTIVITY_TYPE.POS_REFUND].includes(activityType as ACTIVITY_TYPE) && modification > 0)
    ) {
      return coinAdjustmentActiveImg
    } else if (
      coinAdjustmentInActive.includes(activityType) ||
      ([ACTIVITY_TYPE.POS_VOID, ACTIVITY_TYPE.POS_REFUND].includes(activityType as ACTIVITY_TYPE) && modification < 0)
    ) {
      return coinAdjustmentInActiveImg
    } else if (earn.includes(activityType)) {
      return 'earn.png'
    } else if ([...ACTIVITY_REDEEM_TYPE, ACTIVITY_TYPE.POS_SPEND].includes(activityType as ACTIVITY_TYPE)) {
      return 'redeem.png'
    } else if ('Point.Expired' === activityType) {
      return 'expiry.png'
    } else if (
      campaignPoint.includes(activityType) || 
      activityType.match(MallLoyalty) ||
      (
        [ACTIVITY_TYPE.LOYALTYGAME_EARN_GAME].includes(activityType as ACTIVITY_TYPE) &&
        [ACTIVITY_TYPE.LOYALTYGAME_EARN_GAME].includes(modificationType as ACTIVITY_TYPE)
      )
    ) {
      return 'campaign-point.png'
    }
    return modification > 0 ? coinAdjustmentActiveImg : coinAdjustmentInActiveImg
  }

  getTypeOfCoinHistory = (history: History) => {
    const IncentOutcomesEarn = 'Incent.Outcomes.Earn'
    const IncentOutcomes = 'INCENT.Outcomes'
    const MallLoyalty = 'MallLoyalty.Earn'
    const coinAdjustment = [
      'Coin.Behavior.Campaign.Refund',
      'Coin.Manual.PointAdjust.Add',
      'Coin.Manual.PointAdjust.Deduct',
      'Coin.Refund',
      'Behavior.Campaign.Refund',
      'Manual.PointAdjust.Add',
      'Point.Refund',
      'Manual.PointAdjust.Deduct',
    ]
    const earn = [
      'Incent.Outcomes.Earn',
      'Behavior.Campaign.Earn',
      'Behavior.Campaign.Earn',
      'Staff.Point.Earn',
      'Behavior.Campaign.Earn',
      'Campaign.Point.Earn',
      'Survey.Point.Earn',
      'ShopMarathon.Earn.Game',
    ]

    const { activityType, modificationType, pointSourceName } = history

    if (
      modificationType === IncentOutcomes &&
      activityType === IncentOutcomesEarn &&
      pointSourceName.match(this.keywordTMN)?.length
    ) {
      return 'รับคอยน์'
    } else if (
      coinAdjustment.includes(activityType) ||
      activityType.match(this.keywordRevert) ||
      [ACTIVITY_TYPE.POS_REFUND, ACTIVITY_TYPE.POS_VOID].includes(activityType as ACTIVITY_TYPE)
    ) {
      return 'ปรับปรุงคอยน์'
    } else if (
      earn.includes(activityType) || 
      activityType.match(MallLoyalty) ||
      (
        [ACTIVITY_TYPE.LOYALTYGAME_EARN_GAME].includes(activityType as ACTIVITY_TYPE) &&
        [ACTIVITY_TYPE.LOYALTYGAME_EARN_GAME].includes(modificationType as ACTIVITY_TYPE)
      )
    ) {
      return 'รับคอยน์'
    } else if ([...ACTIVITY_REDEEM_TYPE, ACTIVITY_TYPE.POS_SPEND].includes(activityType as ACTIVITY_TYPE) ||
      activityType.match(this.keywordTransfer)
    ) {
      return 'แลกคอยน์'
    } else if ('Point.Expired' === activityType) {
      return 'คอยน์หมดอายุ'
    }
    return 'ปรับปรุงคอยน์'
  }

  onShowModalCalendar() {
    this.store.commit('coinHistory/updateModalCalendarActive', true)
  }

  updateCalendar(list: string[]) {
    this.store.commit('coinHistory/updateCalendar', list)
  }
}
